import axios from "axios";
import { useQuery } from "@tanstack/vue-query";
import { endpoints } from "@/endpoints";
import { tags, unwrapDeepRefs, type MaybeRef } from "@/api/common";
import useApiStore from "@/stores/api";
import type {
  DeviceParams,
  GetDeviceResponse,
  ListDevicesResponse,
  Device,
} from "@/api/devices";
import type { DeepMaybeRef } from "@vueuse/core";
import { useLocationSelector } from "@/composables/location";
import { queryKeys } from "../queryKeys";
import { computed, unref } from "vue";

type ArgsType = {
  filters?: DeepMaybeRef<DeviceParams>;
  enabled?: MaybeRef<boolean>;
  options?: { ignoreLocationGroup: boolean };
};

export const useGetDevices = (args?: ArgsType) => {
  const apiStore = useApiStore();
  const { locationParams, isLocationsLoaded } = useLocationSelector();

  const isEnabled = computed(
    () => unref(args?.enabled) !== false && isLocationsLoaded.value
  );

  const filtersMerged = computed(() => ({
    ...(!args?.options?.ignoreLocationGroup && { ...locationParams.value }),
    ...unref(args?.filters),
  }));

  return useQuery({
    queryKey: queryKeys[tags.platform.devices].list(filtersMerged).queryKey,
    queryFn: async () => {
      const params: DeviceParams = {
        ...unwrapDeepRefs(filtersMerged),
      };

      const result = await axios.get<ListDevicesResponse>(
        apiStore.getEndpoint("platform", endpoints.devices.list),
        { params }
      );
      return result.data;
    },
    select: (data) => ({
      ...data,
      list: data.list.map(
        (device) =>
          <Device>{
            ...device,
            settings: JSON.parse(device.settings),
          }
      ),
    }),
    enabled: isEnabled,
    keepPreviousData: true,
  });
};

export const useGetDevice = (deviceId: MaybeRef<string>) => {
  const apiStore = useApiStore();

  return useQuery({
    queryKey: queryKeys[tags.platform.devices].byId(deviceId).queryKey,
    queryFn: async () => {
      const result = await axios.get<GetDeviceResponse>(
        apiStore.getEndpoint("platform", endpoints.devices.get(unref(deviceId)))
      );
      return result.data;
    },
    select: (data) => data.gadget,
  });
};

export const useGetCompanyDevices = () => {
  const apiStore = useApiStore();

  return useQuery({
    queryKey: queryKeys[tags.platform.devices].list().queryKey,
    queryFn: async () => {
      const result = await axios.get<ListDevicesResponse>(
        apiStore.getEndpoint("platform", endpoints.devices.list)
      );
      return result.data;
    },
  });
};
